import React, { useState, useEffect, useCallback } from "react";
import {
  Menu,
  MoreVertical,
  Home,
  Truck,
  Bell,
  Plus,
  TrendingUpDown,
  User,
  History,
  PhoneCall,
  LogOut,
  Settings,
  ArrowLeft,
  List,
  Heart,
  ShoppingCart,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBuyer } from "../contexts/BuyerContext";
import { useProductState } from "../contexts/ProductContext";
import "./Header.css";
import Logo from "../assets/images/JMIlogo.png";
import { getLastSelectedSegment } from "../utils/segmentCache";

const DESKTOP_COLLAPSED_KEY = "jmi_desktop_sidebar_collapsed";

const Header = ({ title = "Dashboard", showBack = false, onBack }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);

  // Desktop: collapsed = 52px icon rail, expanded = 220px full sidebar
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(DESKTOP_COLLAPSED_KEY);
      if (saved !== null) return saved === "true";
    } catch {}
    return true; // Default to sleek 52px icon rail on desktop
  });

  // Mobile / Tablet (< 1024px): drawer open/closed (hidden by default)
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { buyer, clearBuyer } = useBuyer();

  // Safely get product state badges (Cart/Selections and Likes/Favourites)
  let productState = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    productState = useProductState();
  } catch {}

  const likedProducts = productState?.likedProducts || [];
  const selectedProducts = productState?.selectedProducts || [];

  // Resize listener & CSS variable updater
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);

      if (desktop) {
        setIsMobileDrawerOpen(false);
        const w = isCollapsed ? "52px" : "220px";
        document.documentElement.style.setProperty(
          "--sidebar-current-width",
          w,
        );
      } else {
        document.documentElement.style.setProperty(
          "--sidebar-current-width",
          "0px",
        );
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, [isCollapsed]);

  const toggleSidebar = useCallback(() => {
    if (window.innerWidth >= 1024) {
      setIsCollapsed((prev) => {
        const next = !prev;
        try {
          localStorage.setItem(DESKTOP_COLLAPSED_KEY, String(next));
        } catch {}
        return next;
      });
    } else {
      setIsMobileDrawerOpen((prev) => !prev);
    }
  }, []);

  const handleLogout = () => {
    try {
      navigate("/login");
      clearBuyer();
    } catch (e) {
      console.error("Logout error:", e);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDropdown && !e.target.closest(".header-dropdown-container")) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showDropdown]);

  // Integrated menu items: standard sidebar + footer menu items (Categories, Selections, Favourites)
  const menuItems = [
    { label: "Home", icon: <Home size={19} />, path: "/dashboard" },
    {
      label: "Categories",
      icon: <List size={19} />,
      path: `/productSegment/${getLastSelectedSegment() || "GOLD"}`,
    },
    {
      label: "Selections",
      icon: <ShoppingCart size={19} />,
      path: "/selections",
      badge: selectedProducts.length || null,
    },
    {
      label: "Favourite",
      icon: <Heart size={19} />,
      path: "/favorite",
      badge: likedProducts.length || null,
    },
    { label: "Orders", icon: <Truck size={19} />, path: "/orderflow" },
    {
      label: "Live Rate",
      icon: <TrendingUpDown size={19} />,
      path: "/liverates",
    },
    {
      label: "Requested Orders",
      icon: <Plus size={19} />,
      path: "/orders/requested",
    },
    {
      label: "Rejected Orders",
      icon: <Plus size={19} />,
      path: "/orders/rejected",
    },
    { label: "Offers & Discount", icon: <Bell size={19} />, path: "/offers" },
    { label: "Settings", icon: <Settings size={19} />, path: "/settings" },
  ];

  // User display name / initial
  const buyerName = buyer?.organizationName || buyer?.buyerId || "Buyer";
  const buyerInitial = buyerName ? buyerName.charAt(0).toUpperCase() : "B";

  return (
    <>
      {/* ── MOBILE / TABLET OVERLAY DRAWER ─────────────────────────────────── */}
      {!isDesktop && (
        <>
          {isMobileDrawerOpen && (
            <div
              className="sidebar-backdrop"
              onClick={() => setIsMobileDrawerOpen(false)}
              aria-label="Close sidebar"
            />
          )}
          <aside
            className={`mobile-sidebar ${isMobileDrawerOpen ? "open" : ""}`}
          >
            <div className="mobile-sidebar-header">
              <img src={Logo} alt="JMI Logo" className="mobile-sidebar-logo" />
            </div>
            <nav className="sidebar-nav">
              {menuItems.map((item, idx) => {
                const isActive = location.pathname === item.path;
                return (
                  <div
                    key={idx}
                    className={`sidebar-item ${isActive ? "active" : ""}`}
                    onClick={() => {
                      navigate(item.path);
                      setIsMobileDrawerOpen(false);
                    }}
                  >
                    <span className="sidebar-icon">{item.icon}</span>
                    <span className="sidebar-label">{item.label}</span>
                    {item.badge ? (
                      <span className="sidebar-count-badge">{item.badge}</span>
                    ) : null}
                  </div>
                );
              })}
            </nav>
          </aside>
        </>
      )}

      {/* ── DESKTOP PERSISTENT SIDEBAR (52px Icon Rail or 220px Expanded) ─── */}
      {isDesktop && (
        <aside
          className={`desktop-sidebar ${
            isCollapsed ? "collapsed" : "expanded"
          }`}
        >
          <div className="desktop-sidebar-header">
            {!isCollapsed ? (
              <img src={Logo} alt="JMI Logo" className="desktop-sidebar-logo" />
            ) : (
              <img src={Logo} alt="JMI Logo" className="mobile-sidebar-logo" />
            )}
          </div>

          <nav className="sidebar-nav">
            {menuItems.map((item, idx) => {
              const isActive = location.pathname === item.path;
              return (
                <div
                  key={idx}
                  className={`sidebar-item ${isActive ? "active" : ""} ${
                    isCollapsed ? "icon-only" : ""
                  }`}
                  data-tooltip={item.label}
                  onClick={() => navigate(item.path)}
                  title={isCollapsed ? item.label : undefined}
                >
                  <span className="sidebar-icon">
                    {item.icon}
                    {item.badge && isCollapsed ? (
                      <span className="sidebar-icon-dot" />
                    ) : null}
                  </span>
                  {!isCollapsed && (
                    <span className="sidebar-label">{item.label}</span>
                  )}
                  {!isCollapsed && item.badge ? (
                    <span className="sidebar-count-badge">{item.badge}</span>
                  ) : null}
                  {isActive && isCollapsed && (
                    <span className="active-rail-bar" />
                  )}
                </div>
              );
            })}
          </nav>
        </aside>
      )}

      {/* ── TOP HEADER (APP BAR) ───────────────────────────────────────────── */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            {showBack ? (
              <button
                onClick={onBack || (() => navigate(-1))}
                className="menu-btn"
                aria-label="Go Back"
              >
                <ArrowLeft className="icon" size={22} />
              </button>
            ) : (
              <button
                onClick={toggleSidebar}
                className="menu-btn"
                aria-label="Toggle Sidebar"
                title={
                  isDesktop
                    ? isCollapsed
                      ? "Expand sidebar"
                      : "Collapse sidebar"
                    : "Open menu"
                }
              >
                <Menu className="icon" size={22} />
              </button>
            )}
            <h1 className="header-title">{title}</h1>
          </div>

          {/* Right actions */}
          <div className="header-right">
            <div className="header-dropdown-container">
              <button
                onClick={() => setShowDropdown((prev) => !prev)}
                className="user-profile-trigger"
                aria-label="User account menu"
                aria-expanded={showDropdown}
              >
                <div className="user-avatar-pill">
                  <span className="user-avatar-initial">{buyerInitial}</span>
                  <span className="user-name-label">{buyerName}</span>
                </div>
                <MoreVertical className="icon more-icon" size={18} />
              </button>

              {showDropdown && (
                <div className="header-dropdown-menu" role="menu">
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setShowDropdown(false);
                      navigate("/profile");
                    }}
                    role="menuitem"
                  >
                    <User size={15} /> My Profile
                  </div>
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setShowDropdown(false);
                      navigate("/settings");
                    }}
                    role="menuitem"
                  >
                    <Settings size={15} /> Settings
                  </div>
                  {/* <div
                    className="dropdown-item"
                    onClick={() => { setShowDropdown(false); navigate('/rewards'); }}
                    role="menuitem"
                  >
                    <Trophy size={15} /> Rewards
                  </div> */}
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setShowDropdown(false);
                      navigate("/orderhistory");
                    }}
                    role="menuitem"
                  >
                    <History size={15} /> Order History
                  </div>
                  <div
                    className="dropdown-item"
                    onClick={() => window.location.assign("tel:+919692393933")}
                    role="menuitem"
                  >
                    <PhoneCall size={15} /> Customer Care
                  </div>
                  <div className="dropdown-divider" />
                  <div
                    className="dropdown-item logout-item"
                    onClick={handleLogout}
                    role="menuitem"
                  >
                    <LogOut size={15} /> Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
